import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import ADND from '@aytm/lib/adnd/ADND'
import { Button } from 'primereact/button'
import { CustomDropdown, type CustomDropdownChangeParams, type CustomDropdownType } from '../elements/CustomDropdown'
import { OverlayPanel } from 'primereact/overlaypanel'
import { useExclusiveOverlay, useDashboard } from '@aytm/app/insights/dashboard/components/hooks'
import { useSections } from './SectionToggle.hooks'
import {
  itemOptionTemplate,
  type SectionDropdownOptionItem,
  EditSectionNameForm,
  DragHandle,
  MiniDialog
} from './SectionToggle.elements'
import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'
import type { SectionType } from '@aytm/app/insights/dashboard/models/BlockModel'
import mcss from './SectionToggle.module.scss'
import { isLockedForEditing } from '@aytm/app/insights/dashboard/models/BlockModel.elements'
import type { CaptionType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'

type SectionToggleProps = {
  target: CaptionType['target'] // 'attribute' | 'entity' | 'meta'
  show?: boolean
  caption?: CaptionType['section']
  onExpandChange?(arg0: unknown): void
  onDrag?: {
    (arg0: { action: 'drop'; data: { [key: string]: unknown; newSection: string } }, arg1?: unknown): void
  }
}

export const SectionToggle: React.FC<SectionToggleProps> = props => {
  const { target, show: isExpanded, caption, onDrag } = props
  const isMounted = useRef(false)
  const { isSharedView } = useDashboard()
  const isEditable = useMemo(() => !isLockedForEditing(caption, target), [caption, target])
  const isDraggable = useMemo(() => 'attribute' === target || 'entity' === target, [target])

  const {
    attributes,
    groups,
    getAvailableAttributesForSection,
    onAddAttribute,
    onAddGroup,
    onCreateNewSection,
    onRenameSection,
    onDeleteSection,
    validateSectionName,
    hasSectionItems,
    getCurrentSectionItems,
    onReplaceSection
  } = useSections()

  const isExpandable = useMemo(() => hasSectionItems(caption, target), [caption, target, hasSectionItems])

  const sourceGroups = useMemo(
    // The mapping is valid, GroupModelList is immutable-wrapped, SectionType is plain JS object
    () =>
      ((groups?.toJS?.() ?? []) as undefined as SectionType[]).filter(
        group => group.target === target && Boolean(group.name)
      ),
    [groups, target]
  )

  const currentAttributes = useMemo<string[]>(
    () => getCurrentSectionItems(caption, target),
    [caption, getCurrentSectionItems, target]
  )

  const sourceAttributeKeys = useMemo<string[]>(
    () => getAvailableAttributesForSection(caption, target),
    [getAvailableAttributesForSection, caption, target]
  )

  const options = useMemo<SectionDropdownOptionItem[]>(
    () =>
      sourceGroups
        .filter(group => group.items.some(item => sourceAttributeKeys.includes(item)))
        .map(group => {
          return {
            code: group.name,
            name: decode(group.name),
            isGroup: true,
            itemsInGroup: group.items.length,
            items: group.items
          }
        })
        .concat(
          sourceAttributeKeys.filter(Boolean).map(attribute => {
            return {
              code: attribute,
              name: decode(attributes[attribute].text),
              isGroup: false,
              itemsInGroup: 0,
              items: [] as string[]
            }
          })
        ),
    [sourceAttributeKeys, sourceGroups, attributes]
  )

  const sectionToggleRef = useRef<CustomDropdownType>(null)

  const onValueChange = useCallback(
    (event: CustomDropdownChangeParams) => {
      event.stopPropagation()
      event.preventDefault()
      const [value] = event.value as SectionDropdownOptionItem[]
      if (value.isGroup) {
        onAddGroup(caption, value.code, target)
      } else {
        onAddAttribute(caption, value.code, target)
      }
      sectionToggleRef.current?.hide?.()
    },
    [caption, onAddGroup, onAddAttribute, target]
  )

  const [isMiniOverlayActive, setIsMiniOverlayActive] = useState(false)

  const overlayRef = useRef<OverlayPanel>(null)
  const exclusiveOverlay = useExclusiveOverlay()

  const handleDismiss = () => {
    exclusiveOverlay(overlayRef.current, overlay => overlay?.hide())
  }

  const [initialSectionName, setInitialSectionName] = useState('')

  const handleSectionNameReady = useCallback(
    (name: string) => {
      if (initialSectionName && initialSectionName !== name) {
        onRenameSection(initialSectionName, name)
      } else {
        onCreateNewSection(name, target, caption)
      }
    },
    [onCreateNewSection, onRenameSection, target, caption, initialSectionName]
  )

  const handleAuxAction = useCallback(() => {
    props.onExpandChange?.({ section: caption, target, show: !isExpanded })
  }, [isExpanded, props, caption, target])

  const [overlayContent, setOverlayContent] = useState<string>(null)
  const handleAddNewSectionRequest = useCallback(() => {
    if (isMiniOverlayActive) {
      return
    }
    setInitialSectionName('')

    setOverlayContent('section-edit')
    exclusiveOverlay(null)
    overlayRef.current?.show(null, sectionToggleRef.current?.getElement?.())
  }, [isMiniOverlayActive, exclusiveOverlay])
  // }, [exclusiveOverlay, isMiniOverlayActive])

  const handleConfirmDelete = useCallback(() => {
    onDeleteSection(caption, target)
    exclusiveOverlay(overlayRef.current, overlay => overlay?.hide())
  }, [onDeleteSection, caption, target, exclusiveOverlay])

  const onDeleteSectionHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault()
      if (currentAttributes.length) {
        setOverlayContent('section-delete')
        exclusiveOverlay(overlayRef.current, overlay => overlay?.show(null, sectionToggleRef.current?.getElement?.()))
      } else {
        handleConfirmDelete()
      }
    },
    [currentAttributes.length, exclusiveOverlay, handleConfirmDelete]
  )

  const handleEditSectionName = useCallback(() => {
    if (isMiniOverlayActive) {
      return
    }
    setInitialSectionName(caption)
    sectionToggleRef.current?.hide?.()
    setOverlayContent('section-edit')
    exclusiveOverlay(overlayRef.current, overlay => overlay?.show(null, sectionToggleRef.current?.getElement?.()))
  }, [isMiniOverlayActive, caption, exclusiveOverlay])

  const [isBeingDragged, setIsBeingDragged] = useState(false)
  const handleOnDrag: { (data: { action: 'start' | 'end'; dragType: string; dragData: unknown }): void } = data => {
    setIsBeingDragged('start' === data.action)
  }

  useEffect(() => {
    isMounted.current = true
    if (!isMounted.current || !isMiniOverlayActive) {
      return void 0
    }
    overlayRef.current?.getElement()?.querySelector('input')?.focus?.()
    return () => {
      isMounted.current = false
    }
  }, [isMiniOverlayActive])

  const [isDragEntered, setIsDragEntered] = useState(false)

  const dragSource = useMemo(
    () =>
      ADND.targets
        .data({
          acceptTypes: ['section', 'attribute']
        })
        .onEnter((api, event) => {
          const data: null | string | Record<string, unknown> = api.getData()
          if (data === caption) {
            return
          }

          if ('attribute' === event.type && typeof data !== 'string' && !currentAttributes.length) {
            // section receives an attribute
            if (data.target === target) {
              setIsDragEntered(true)
              onDrag?.({ action: 'drop', data: { ...(data || {}), newSection: caption } })
            } else {
              setIsDragEntered(false)
            }
          } else {
            setIsDragEntered(true)
          }
        })
        .onLeave(() => {
          setIsDragEntered(false)
        })
        .onDrop(function (event) {
          setIsDragEntered(false)
          if (event.data === caption) {
            return
          }
          onReplaceSection(caption, event.data as string)
        }),
    [caption, currentAttributes.length, onDrag, onReplaceSection, target]
  )

  return (
    <div
      className={classNames(mcss['section-toggle-container'], {
        draggable: isDraggable,
        glow: isDragEntered,
        ['semi-transparent']: isBeingDragged
      })}
      ref={node => (isDraggable ? void dragSource.connect(node) : void 0)}
    >
      {isEditable && !isMiniOverlayActive && (
        <i className={`icon-aytmfd-delete ${mcss['delete-trigger']}`} onClick={onDeleteSectionHandler} />
      )}
      <CustomDropdown
        ref={sectionToggleRef}
        className={classNames(mcss['section-toggle'], { glow: isDragEntered })}
        panelClassName={classNames('insights-portal', mcss['section-toggle-panel'])}
        dropdownIcon={'pi icon-aytmfd-plus-medium'}
        auxActionIcon={
          isExpandable ? (isExpanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right') : 'pi pi-chevron-right p-disabled'
        }
        auxActionClassName={'section-toggle-aux-action'}
        onChange={onValueChange}
        onAuxTriggerClick={isExpandable ? handleAuxAction : null}
        onLabelClick={isEditable ? handleEditSectionName : null}
        value={null}
        disabled={isMiniOverlayActive || isSharedView}
        options={options}
        optionLabel="name"
        filter
        filterBy="name"
        resetFilterOnHide={true}
        filterPlaceholder="Search groups or attributes"
        showClear={false}
        placeholder={caption}
        appendTo={document.body}
        panelFooterTemplate={
          'meta' === target ? null : (
            <div className={mcss['section-toggle-panel-footer-buttons']}>
              <Button type="button" className="p-button-link" onClick={handleAddNewSectionRequest}>
                Add new section
              </Button>
            </div>
          )
        }
        itemTemplate={itemOptionTemplate}
      />
      {isDraggable && !isMiniOverlayActive && (
        <DragHandle dragType="section" dragData={caption} onDrag={handleOnDrag} parent={1} child={isEditable ? 1 : 0} />
      )}
      <OverlayPanel
        ref={overlayRef}
        dismissable
        showCloseIcon={false}
        appendTo={document.body}
        onShow={() => setIsMiniOverlayActive(true)}
        onHide={() => setIsMiniOverlayActive(false)}
        // dismissable={false}
        className={classNames('PRAYTMTheme insights-portal', mcss['create-section'])}
      >
        {'section-edit' === overlayContent && (
          <EditSectionNameForm
            onNameReady={handleSectionNameReady}
            onDismiss={handleDismiss}
            onNameValidate={validateSectionName}
            initialValue={initialSectionName}
            target={target}
          />
        )}
        {'section-delete' === overlayContent && (
          <MiniDialog
            message={
              <>
                {`Are you sure to remove this section containing ${currentAttributes.length}`}&nbsp;
                {`attribute${currentAttributes.length > 1 ? 's' : ''}?`}
              </>
            }
            onDismiss={handleDismiss}
            onConfirm={handleConfirmDelete}
          />
        )}
      </OverlayPanel>
    </div>
  )
}
SectionToggle.displayName = 'SectionToggle'
